/*
 * Theme cross-sell tabs ("More Classics You'll Love").
 * Used by the product page (sections/theme-cross-sell.liquid, grid cards from
 * theme-cross-sell-card) and the cart drawer (snippets/cart-drawer-recommendations.liquid,
 * carousel cards from cart-upsell-card); tabs/panels from snippets/theme-cross-sell-tabs.liquid.
 *
 * Random per visitor: when the element nears the viewport, loads the handle pool of
 * every (theme tag, type tag) pair (sections/theme-cross-sell-pool.liquid), drops the
 * excluded handles (current product / cart items), shuffles, and renders N random
 * cards per tab from /products/<handle>?section_id=<data-card-section>.
 * The opening tab renders first; the others render right after in the background.
 * Tabs with an empty pool are removed; the element stays hidden if all are empty.
 * Fires `theme-cross-sell:change` whenever the visible panel changes or fills.
 *
 * Speed: responses are no-store, so they are also kept in sessionStorage for 10 minutes
 * (page changes reuse them). `data-eager` (cart drawer) loads at page idle instead of on
 * view, so the block is ready when the drawer opens; other instances (product page) warm
 * their pools at idle, plus the drawer's cards (`data-drawer-card-section`, empty cart only)
 * so the drawer opened by Add to cart renders at once. Picks prefer cards already loaded on
 * this page, so drawer re-renders after a cart change don't wait either.
 */

// Guarded: the script is included by both the product section and the cart drawer.
if (!customElements.get('theme-cross-sell')) {
  // Responses are shared across instances for the page's lifetime: the cart drawer
  // re-renders (new element) on every cart change and must not refetch the same pools/cards.
  const themeCrossSellCache = new Map();
  const themeCrossSellTtl = 10 * 60 * 1000;
  // Prices and markup differ per theme, currency and language: keep them apart.
  const themeCrossSellScope = [window.Shopify?.theme?.id, window.Shopify?.currency?.active, document.documentElement.lang].join('|');

  function themeCrossSellStored(url) {
    try {
      const entry = JSON.parse(sessionStorage.getItem(`tcs:${url}`));
      if (entry && entry.scope === themeCrossSellScope && Date.now() - entry.time < themeCrossSellTtl) return entry.text;
    } catch (error) {}
    return '';
  }

  function themeCrossSellStore(url, text) {
    try {
      sessionStorage.setItem(`tcs:${url}`, JSON.stringify({ scope: themeCrossSellScope, time: Date.now(), text }));
    } catch (error) {}
  }

  function themeCrossSellFetchText(url) {
    if (!themeCrossSellCache.has(url)) {
      const stored = themeCrossSellStored(url);
      themeCrossSellCache.set(
        url,
        stored
          ? Promise.resolve(stored)
          : fetch(url)
              .then((response) => (response.ok ? response.text() : ''))
              .catch(() => '')
              .then((text) => {
                if (text) themeCrossSellStore(url, text);
                else themeCrossSellCache.delete(url); // retry failures next time
                return text;
              })
      );
    }
    return themeCrossSellCache.get(url);
  }

  function themeCrossSellFetch(url) {
    return themeCrossSellFetchText(url).then((text) => new DOMParser().parseFromString(text, 'text/html'));
  }

  // Run after the page has loaded, when the browser is idle.
  function themeCrossSellWhenIdle(callback) {
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(callback, { timeout: 2000 }) : setTimeout(callback, 200));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  customElements.define(
    'theme-cross-sell',
    class ThemeCrossSell extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.picks = new Map(); // tab -> shuffled handles to show
        this.rendered = new Map(); // tab -> render promise

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab));
          tab.addEventListener('keydown', (event) => this.onKeydown(event));
        });

        if (this.hasAttribute('data-eager')) {
          // The drawer re-renders on every cart change: reload at once (cache hits) once the page is up.
          if (document.readyState === 'complete') this.load();
          else themeCrossSellWhenIdle(() => this.isConnected && this.load());
        } else {
          themeCrossSellWhenIdle(() => this.prefetch());
        }

        // Also load when it comes into view (drawer opened before the page finished loading).
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this.parentElement || this);
      }

      async prefetch() {
        const themes = this.dataset.themes.split(',').filter(Boolean);
        this.tabs.forEach((tab) => themes.forEach((theme) => this.fetchPool(theme, tab.dataset.typeHandle)));

        const section = this.dataset.drawerCardSection;
        if (!section || this.tabs.length === 0) return;
        const tab = this.tabs.find((other) => other.getAttribute('aria-selected') === 'true') || this.tabs[0];
        const handles = await this.poolHandles(tab, themes);
        this.pick(handles, parseInt(this.dataset.drawerLimit, 10) || 10, section).forEach((handle) =>
          themeCrossSellFetchText(this.cardUrl(handle, section))
        );
      }

      async poolHandles(tab, themes) {
        const pools = await Promise.all(themes.map((theme) => this.fetchPool(theme, tab.dataset.typeHandle)));
        const exclude = new Set(this.dataset.exclude.split(','));
        return [...new Set(pools.flat())].filter((handle) => !exclude.has(handle));
      }

      // Random picks; cards already loaded on this page come first so they show without waiting.
      pick(handles, limit, section = this.dataset.cardSection) {
        const shuffled = this.shuffle(handles);
        const loaded = shuffled.filter((handle) => themeCrossSellCache.has(this.cardUrl(handle, section)));
        return [...loaded, ...shuffled.filter((handle) => !loaded.includes(handle))].slice(0, limit);
      }

      cardUrl(handle, section = this.dataset.cardSection) {
        return `${this.dataset.productUrl}/${handle}?section_id=${section}`;
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async load() {
        if (this.loading) return;
        this.loading = true;
        const themes = this.dataset.themes.split(',').filter(Boolean);
        const limit = parseInt(this.dataset.limit, 10) || 8;

        await Promise.all(
          this.tabs.map(async (tab) => {
            const handles = await this.poolHandles(tab, themes);

            if (handles.length === 0) {
              this.panelFor(tab).remove();
              tab.remove();
              return;
            }
            this.picks.set(tab, this.pick(handles, limit));
          })
        );

        this.tabs = this.tabs.filter((tab) => tab.isConnected);
        if (this.tabs.length === 0) return;

        const opening = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        this.select(opening, false);
        await this.render(opening);
        this.hidden = false;

        this.tabs.forEach((tab) => this.render(tab));
      }

      async fetchPool(theme, type) {
        const html = await themeCrossSellFetch(`${this.dataset.poolUrl}/${theme}+${type}?section_id=theme-cross-sell-pool`);
        try {
          return JSON.parse(html.querySelector('[data-cross-sell-pool]')?.textContent || '[]');
        } catch (error) {
          return [];
        }
      }

      async fetchCard(handle) {
        const html = await themeCrossSellFetch(this.cardUrl(handle));
        const card = html.querySelector('li[data-handle]');
        return card ? document.importNode(card, true) : null;
      }

      // Render a tab's cards once; later calls reuse the same promise.
      render(tab) {
        if (!this.rendered.has(tab)) {
          this.rendered.set(
            tab,
            Promise.all(this.picks.get(tab).map((handle) => this.fetchCard(handle))).then((cards) => {
              const panel = this.panelFor(tab);
              panel.replaceChildren(...cards.filter(Boolean));
              // The first cards are on screen as soon as the block shows: don't wait for lazy loading.
              panel.querySelectorAll('img[loading="lazy"]').forEach((image, index) => {
                if (index < 2) image.loading = 'eager';
              });
              // Injected cards carry Dawn's reveal-on-scroll classes; register them or they stay invisible.
              if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(panel);
              this.notifyChange();
            })
          );
        }
        return this.rendered.get(tab);
      }

      // Fisher-Yates shuffle (returns a new array).
      shuffle(items) {
        const list = items.slice();
        for (let i = list.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [list[i], list[j]] = [list[j], list[i]];
        }
        return list;
      }

      panelFor(tab) {
        return this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
      }

      notifyChange() {
        this.dispatchEvent(new CustomEvent('theme-cross-sell:change', { bubbles: true }));
      }

      select(tab, focus = true) {
        this.tabs.forEach((other) => {
          const active = other === tab;
          other.setAttribute('aria-selected', String(active));
          other.tabIndex = active ? 0 : -1;
          this.panelFor(other).hidden = !active;
        });
        if (focus) tab.focus();
        this.notifyChange();
      }

      onKeydown(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        const last = this.tabs.length - 1;
        const next = {
          ArrowRight: index === last ? 0 : index + 1,
          ArrowLeft: index === 0 ? last : index - 1,
          Home: 0,
          End: last,
        }[event.key];

        if (next === undefined) return;
        event.preventDefault();
        this.select(this.tabs[next]);
      }
    }
  );
}
